import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export const strictEmailValidator: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null => {
  const raw = (control.value ?? '').trim();
  if (!raw) {
    return null;
  }

  if (raw.length > 254) {
    return { invalidEmail: true };
  }

  const re =
    /^(?=.{1,254}$)[a-zA-Z0-9](\.?[\w\-+])*@[a-zA-Z0-9](?:[a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9\-]{0,61}[a-zA-Z0-9]))*\.[a-zA-Z]{2,}$/;
  return re.test(raw) ? null : { invalidEmail: true };
};

export function formatNationalNumber(prefix: string, number: string): string {
  const raw = number.replace(/\D/g, '');

  switch (prefix) {
    case '+34': // España → 3 2 2 2
      return raw.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1 $2 $3 $4');

    case '+33': // Francia → 1 2 2 2 2
      return raw.replace(
        /(\d{1})(\d{2})(\d{2})(\d{2})(\d{2})/,
        '$1 $2 $3 $4 $5'
      );

    case '+44': // UK → 4 3 3
      return raw.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3');

    default:
      return raw;
  }
}

export function dynamicPhoneValidator(getPrefix: () => string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;

    const prefix = getPrefix();
    const digitsOnly = value.replace(/[^\d]/g, '');

    switch (prefix) {
      case '+34': // España: 9 dígitos, empieza por 6, 7 o 9
        return /^[679]\d{8}$/.test(digitsOnly.slice(2))
          ? null
          : { invalidPhone: 'Número español no válido' };

      case '+33': // Francia: 9 dígitos, empieza por 6 o 7
        return /^[67]\d{8}$/.test(digitsOnly.slice(2))
          ? null
          : { invalidPhone: 'Numéro français invalide' };

      case '+44': // UK: 10 dígitos, empieza por 7 (móvil)
        return /^7\d{9}$/.test(digitsOnly.slice(2))
          ? null
          : { invalidPhone: 'UK phone number invalid' };

      default:
        return { invalidPhone: 'Prefijo no soportado' };
    }
  };
}

/**
 * Valida un IBAN (cualquier país SEPA).
 * 1. Quita espacios / símbolos.
 * 2. Comprueba patrón básico.
 * 3. Rota los 4 primeros caracteres al final.
 * 4. Convierte letras → números (A=10 … Z=35).
 * 5. Calcula el módulo 97 mediante iteración dígito-a-dígito
 *    para evitar desbordes y que funcione en todos los navegadores.
 */
export function ibanValidator(
  control: AbstractControl
): ValidationErrors | null {
  const raw = (control.value || '').toString();
  const iban = raw.replace(/[^A-Z0-9]/gi, '').toUpperCase();
  if (!iban) {
    // campo vacío → válido cuando no es requerido
    return null;
  }
  // formato básico
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) {
    return { invalidIban: true };
  }
  // mueve 4 primeros al final
  let rearranged = iban.slice(4) + iban.slice(0, 4);
  // letras → números
  const numeric = rearranged
    .split('')
    .map((ch: string) => {
      const code = ch.charCodeAt(0);
      return code >= 65 && code <= 90 ? (code - 55).toString() : ch;
    })
    .join('');
  // cálculo mod 97 en bloques
  let remainder = '';
  for (let i = 0; i < numeric.length; i += 7) {
    remainder = String(parseInt(remainder + numeric.substr(i, 7), 10) % 97);
  }
  return parseInt(remainder, 10) === 1 ? null : { invalidIban: true };
}

export function swiftValidator(
  control: AbstractControl
): ValidationErrors | null {
  const raw = (control.value || '').toString().trim().toUpperCase();
  if (!raw) {
    return null;
  }
  const isValid = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(raw);
  return isValid ? null : { invalidSwift: true };
}
